import { Card, CardDescription, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Link, Navigate } from "@tanstack/react-router";
import { ArrowRight, Building2 } from "lucide-react";

import { useTenantRole } from "../lib/useTenantRole";
import { TenantsPage } from "./Tenants";

/**
 * Home branches by standing: platform admins get the full tenant table (create,
 * storefront summary), a one-tenant member goes straight into their tenant, a
 * multi-tenant member picks, and a member of nothing gets told why it's empty.
 */
export function HomePage() {
  const { query, isPlatformAdmin, tenants } = useTenantRole();

  if (query.isLoading || !query.data) {
    return (
      <div className="flex min-h-64 items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (isPlatformAdmin) return <TenantsPage />;

  if (tenants.length === 1 && tenants[0]) {
    return <Navigate to="/tenants/$tenantId" params={{ tenantId: tenants[0].id }} />;
  }

  if (tenants.length === 0) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>No tenants yet</CardTitle>
            <CardDescription>
              Your account doesn't belong to any tenant. Ask a tenant admin to invite you — the
              invite link lands you here with access.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your tenants</h1>
        <p className="text-sm text-muted-foreground">Pick a tenant to manage.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {tenants.map((tenant) => (
          <Card key={tenant.id} className="transition-colors hover:border-primary/50">
            <Link to="/tenants/$tenantId" params={{ tenantId: tenant.id }} className="block">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="size-4 text-muted-foreground" />
                  {tenant.name}
                  <ArrowRight className="ml-auto size-4 text-muted-foreground" />
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <span className="font-mono text-xs">{tenant.slug}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs capitalize">
                    {tenant.role}
                  </span>
                </CardDescription>
              </CardHeader>
            </Link>
          </Card>
        ))}
      </div>
    </div>
  );
}
